import React, { useState } from 'react';
import moment from 'moment';
import { Dropdown, Form } from 'antd';
import { noop } from 'lodash';

import {
  AppContent,
  BaseButton,
  DateNavigationTitle,
  Icon,
} from '@common/components';
import {
  RenderFilteredTable,
  RenderTableColumn,
} from 'works/components/filtered-table/render-filtered-table';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { useMutation, useQuery } from '@common/hooks';
import { TimekeepingHolidays, UpdateHolidayStatus } from '@timekeeping/graphql';
import { TimekeepingHolidayFragment } from '@common/graphql/schema';
import { HolidayForm } from '@timekeeping/components';
import { AutoAnimatedHeight } from 'works/components/auto-animated-height';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

const NO_FILTERS = {};

export const AdminTimesheetHolidays = () => {
  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TIMEKEEPING.LABEL,
      path: ORGANIZATION_ADMIN_MENU.TIMEKEEPING.ROUTE,
    },
  ]);

  const loading = false;
  const [form] = Form.useForm();
  const [editing, setEditing] = useState<TimekeepingHolidayFragment | null>(
    null
  );
  const [year, setYear] = useState(() => moment().year());
  const query = useQuery(TimekeepingHolidays, { variables: { year } });
  const page = query.data?.page;
  const [updateHolidayStatus] = useMutation(UpdateHolidayStatus);

  const onToggleHoliday = async (holiday: TimekeepingHolidayFragment) => {
    try {
      await updateHolidayStatus({
        variables: {
          id: holiday.id,
          status: holiday.status === 'enabled' ? 'disabled' : 'enabled',
        },
      }).then((res) => {
        return res.data?.update && !res.data.update.errors?.length
          ? Promise.resolve(res)
          : Promise.reject(res.errors || res.data?.update?.errors);
      });

      createMessage('success', 'Holiday successfully updated.');
    } catch (e) {
      createMessage('error', 'Failed to update holiday. Please try again.');
    }
  };

  const editHoliday = (holiday: TimekeepingHolidayFragment) => {
    setEditing(holiday);
    form.setFieldsValue({
      id: holiday.id,
      holidayName: holiday.name,
      startDay: moment(holiday.startDay),
      startTime: moment(`2000-01-01 ${holiday.startTime}`),
      endDay: moment(holiday.endDay),
      endTime: moment(`2000-01-01 ${holiday.endTime}`),
    });
  };

  const tableColumns: RenderTableColumn<TimekeepingHolidayFragment>[] = [
    {
      title: 'Holiday Name',
      key: 'holidayName',
      dataIndex: 'name',
      className: 'holiday-name-col',
      width: '50%',
    },
    {
      title: 'Start Date',
      key: 'startDay',
      dataIndex: 'startDay',
      className: 'start-day-col',
      render: (date) => <div>{moment(date).format('M/D')}</div>,
    },
    {
      title: 'Start Time',
      key: 'startTime',
      dataIndex: 'startTime',
      className: 'start-time-col',
    },
    {
      title: 'End Date',
      key: 'endDay',
      dataIndex: 'endDay',
      className: 'end-day-col',
      render: (date) => <div>{moment(date).format('M/D')}</div>,
    },
    {
      title: 'End Time',
      key: 'endTime',
      dataIndex: 'endTime',
      className: 'end-time-col',
    },
    {
      title: '',
      key: 'status',
      className: 'status-col',
      width: '250px',
      render: (_, holiday) => {
        const enabled = holiday.status === 'enabled';
        const items = enabled
          ? [{ label: 'Disable Holiday', key: 'disable' }]
          : [{ label: 'Enable Holiday', key: 'enable' }];

        return (
          <div>
            <Dropdown
              disabled={holiday.inPast}
              trigger={['click']}
              menu={{ items, onClick: () => onToggleHoliday(holiday) }}
            >
              <BaseButton>
                <div className="d-flex flex-row items-center">
                  <Icon
                    size="xs"
                    icon="circle"
                    color={enabled ? 'grassMid' : 'steelDarker'}
                  />
                  <span>{enabled ? 'Enabled' : 'Disabled'}</span>
                  <Icon size="sm" icon="chevronDown" color="midnightLightest" />
                </div>
              </BaseButton>
            </Dropdown>
            <span style={{ paddingLeft: '24px' }}>
              <BaseButton
                type="link"
                onClick={() => editHoliday(holiday)}
                disabled={holiday.inPast}
              >
                Edit
              </BaseButton>
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <AppContent className="admin-timesheet-holidays-page">
      <DateNavigationTitle
        loading={query.loading}
        disablePrev={page?.hasPrev === false || !!editing}
        disableNext={page?.hasNext === false || !!editing}
        onPrev={() => {
          if (page?.hasPrev === true) {
            setYear((year) => year - 1);
          }
        }}
        onNext={() => {
          if (page?.hasNext === true) {
            setYear((year) => year + 1);
          }
        }}
      >
        {year} Holidays
      </DateNavigationTitle>
      <AutoAnimatedHeight visible={!!editing}>
        <HolidayForm
          editedHoliday={editing}
          form={form}
          onClose={() => setEditing(null)}
        />
      </AutoAnimatedHeight>
      <RenderFilteredTable
        rowKey="id"
        className="invoice-items-list"
        dataSource={page?.holidays || []}
        columns={tableColumns}
        loading={loading}
        onClearFilter={noop}
        onClearAllFilters={noop}
        filters={NO_FILTERS}
        pagination={false}
        rowClassName={(record) =>
          record?.status === 'enabled' ? undefined : 'disabled-row'
        }
      />
    </AppContent>
  );
};
